<template>
  <checkout-section-card v-if="checkoutStore.addons.length > 0">
    <h2
      class="mb-5 flex items-center gap-2.5 text-xl font-semibold uppercase tracking-wide text-mouse"
    >
      <fa-icon :icon="faPuzzlePiece" class="size-5 text-rhino" />
      {{ t('checkout/configure/addons/title') }}
    </h2>

    <div class="flex flex-col gap-3">
      <div
        v-for="addon in checkoutStore.addons"
        :key="addon.id"
        class="rounded-lg border-2 p-4 transition-all duration-200"
        :class="optionCardClass(isSelected(addon.id))"
      >
        <div
          class="flex items-center justify-between cursor-pointer"
          @click="onToggle(addon.id)"
        >
          <div class="flex flex-col gap-1">
            <span
              :id="`addon-name-${addon.id}`"
              :class="isSelected(addon.id) ? 'text-brand' : 'text-mouse'"
            >
              {{ addon.name }}
            </span>
            <span v-if="addon.description" class="text-rhino">
              {{ addon.description }}
            </span>
          </div>

          <div class="flex items-center gap-3">
            <!-- Price -->
            <span
              v-if="addon.price"
              class="font-medium text-rhino whitespace-nowrap"
            >
              {{ format(addon.price) }}
            </span>

            <!-- Toggle switch -->
            <button
              type="button"
              role="switch"
              :aria-checked="isSelected(addon.id)"
              :aria-labelledby="`addon-name-${addon.id}`"
              class="relative h-5 w-9 shrink-0 rounded-full border bg-depth-0 box-content transition-colors"
              :class="isSelected(addon.id) ? 'border-brand' : 'border-rhino/40'"
              @click.stop="onToggle(addon.id)"
            >
              <span
                class="pointer-events-none absolute -top-px -left-px size-5.5 rounded-full transition-transform"
                :class="
                  isSelected(addon.id)
                    ? 'translate-x-4 bg-brand'
                    : 'translate-x-0 bg-rhino'
                "
              ></span>
            </button>
          </div>
        </div>

        <!-- Quantity selector for per_unit addons -->
        <div
          v-if="isSelected(addon.id) && addon.pricingModel !== 'flat_fee'"
          class="mt-3 flex items-center gap-3"
          @click.stop
        >
          <label :for="`addon-qty-${addon.id}`" class="text-sm text-rhino">
            {{ t('checkout/configure/addons/quantity') }}
          </label>
          <div class="flex items-center gap-1">
            <button
              type="button"
              class="flex h-7 w-7 items-center justify-center rounded bg-depth-2 text-mouse hover:bg-depth-3 transition-colors"
              :disabled="getQuantity(addon.id) <= 1"
              @click="onQuantityChange(addon.id, getQuantity(addon.id) - 1)"
            >
              &minus;
            </button>
            <input
              :id="`addon-qty-${addon.id}`"
              type="number"
              :value="getQuantity(addon.id)"
              min="1"
              class="h-7 w-14 rounded border border-depth-2 bg-transparent text-center text-sm text-mouse [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              @change="onQuantityInput(addon.id, $event)"
            />
            <button
              type="button"
              class="flex h-7 w-7 items-center justify-center rounded bg-depth-2 text-mouse hover:bg-depth-3 transition-colors"
              @click="onQuantityChange(addon.id, getQuantity(addon.id) + 1)"
            >
              +
            </button>
          </div>
        </div>
      </div>
    </div>
  </checkout-section-card>
</template>

<script setup lang="ts">
import { faPuzzlePiece } from '@fortawesome/pro-solid-svg-icons';
import { optionCardClass } from '~/helpers/checkout';
const { t } = useI18n();
const checkoutStore = useCheckoutStore();
const { format } = useNumberMoneyFormatting();

function isSelected(addonId: string): boolean {
  return checkoutStore.selectedAddons.has(addonId);
}

function getQuantity(addonId: string): number {
  return checkoutStore.selectedAddons.get(addonId) ?? 1;
}

async function onToggle(addonId: string): Promise<void> {
  await checkoutStore.toggleAddon(addonId);
}

async function onQuantityChange(addonId: string, qty: number): Promise<void> {
  await checkoutStore.setAddonQuantity(addonId, Math.max(1, Math.floor(qty)));
}

async function onQuantityInput(addonId: string, event: Event): Promise<void> {
  const value = parseInt((event.target as HTMLInputElement).value, 10);
  if (Number.isNaN(value)) return;
  await checkoutStore.setAddonQuantity(addonId, Math.max(1, value));
}
</script>
